// POLITICA DE ACESSO — fonte unica.
//
// Nenhuma rota decide sozinha quem pode o que: toda rota consulta esta matriz.
// O frontend pode usar isto para esconder botao, mas o backend SEMPRE revalida —
// esconder nao e proteger (PADRAO §4.4).

/**
 * Tres perfis, e a diferenca entre os dois primeiros e o terceiro importa.
 *
 * `admin_geral` e `familiar` ESCREVEM: cada linha que eles criam leva o nome
 * deles junto, e e por isso que so quem realmente registra pode registrar.
 * `leitor` so ve — e quem acompanha, quer saber como ela esta, mas nao e quem
 * anota. Sem essa separacao, qualquer um poderia alterar um dado e a assinatura
 * de quem lancou deixaria de significar alguma coisa.
 */
export type Perfil = 'admin_geral' | 'familiar' | 'leitor';

export type Recurso =
  | 'leitura'
  | 'anotacao'
  | 'ficha'
  | 'plantao'
  | 'intercorrencia'
  | 'paciente'
  | 'aparelho'
  | 'usuario'
  | 'auditoria'
  | 'configuracao'
  | 'roteiro'
  | 'gravacao';

export type Acao = 'ler' | 'criar' | 'atualizar' | 'excluir';

type Matriz = Record<Recurso, Record<Acao, readonly Perfil[]>>;

/** Todo mundo ve. Ver nao estraga nada, e a familia inteira precisa saber. */
const TODOS = ['admin_geral', 'familiar', 'leitor'] as const;
/** Quem escreve: so quem assina o que escreveu. */
const QUEM_REGISTRA = ['admin_geral', 'familiar'] as const;
const SO_ADMIN = ['admin_geral'] as const;

export const MATRIZ_ACESSO: Matriz = {
  // Registrar medida e anotar sao o coracao do uso diario: os dois perfis fazem.
  leitura: { ler: TODOS, criar: QUEM_REGISTRA, atualizar: SO_ADMIN, excluir: SO_ADMIN },
  anotacao: { ler: TODOS, criar: QUEM_REGISTRA, atualizar: SO_ADMIN, excluir: SO_ADMIN },

  // Ficha (medicacoes, dispositivos, culturas, exames): quem esta no quarto
  // copia do prontuario, e nao e so o admin que vai ao hospital. Ninguem exclui
  // — medicacao encerrada e cateter retirado continuam explicando o passado.
  ficha: { ler: TODOS, criar: QUEM_REGISTRA, atualizar: QUEM_REGISTRA, excluir: [] },

  // Plantao: quem esta no quarto na troca de turno e quem anota o nome de quem
  // assumiu. Nao se apaga — o historico existe justamente para responder "quem
  // estava de servico naquela hora".
  plantao: { ler: TODOS, criar: QUEM_REGISTRA, atualizar: QUEM_REGISTRA, excluir: [] },

  // Intercorrencia: quem esta no quarto e quem VE o erro acontecer, e por isso
  // registra. Nao se exclui nem se edita depois — um registro de erro que pode
  // ser reescrito nao serve para nada, e o valor dele esta justamente em ter
  // sido escrito na hora, por quem estava la.
  intercorrencia: { ler: TODOS, criar: QUEM_REGISTRA, atualizar: [], excluir: [] },

  paciente: { ler: TODOS, criar: SO_ADMIN, atualizar: SO_ADMIN, excluir: SO_ADMIN },
  aparelho: { ler: TODOS, criar: SO_ADMIN, atualizar: SO_ADMIN, excluir: SO_ADMIN },

  // Quem mexe em conta, auditoria e limiar de alerta e so o admin.
  usuario: { ler: SO_ADMIN, criar: SO_ADMIN, atualizar: SO_ADMIN, excluir: SO_ADMIN },
  auditoria: { ler: SO_ADMIN, criar: SO_ADMIN, atualizar: [], excluir: [] },
  configuracao: { ler: TODOS, criar: SO_ADMIN, atualizar: SO_ADMIN, excluir: SO_ADMIN },

  // Perguntas da visita (27/09). O roteiro, o admin escreve a partir dos
  // exames; quem esta no quarto marca como cada pergunta foi respondida.
  roteiro: { ler: TODOS, criar: SO_ADMIN, atualizar: QUEM_REGISTRA, excluir: SO_ADMIN },
  // A gravacao da resposta: quem esta no quarto grava, mas OUVIR e so o admin
  // — e a voz de quem respondeu, o dado mais sensivel do sistema (decisao do
  // Ilton, 27/09). A transcricao vem junto do roteiro, que todos leem.
  // Transcrever de novo e escrever a analise: admin.
  gravacao: { ler: SO_ADMIN, criar: QUEM_REGISTRA, atualizar: SO_ADMIN, excluir: SO_ADMIN },
};

/** Atalho para a tela: esconder botao que so daria 403 ao ser tocado. */
export const podeEscrever = (perfil: Perfil): boolean =>
  perfil === 'admin_geral' || perfil === 'familiar';

export function podeFazer(perfil: Perfil, recurso: Recurso, acao: Acao): boolean {
  return MATRIZ_ACESSO[recurso][acao].includes(perfil);
}

// A auditoria e append-only: nem o admin altera ou apaga registro dela. O
// usuario de runtime do banco tambem nao recebe UPDATE/DELETE nessa tabela.
export const RECURSOS_APPEND_ONLY: readonly Recurso[] = ['auditoria'];

// Recursos que ninguem apaga, nem o admin. Na ficha, apagar destruiria a
// explicacao do passado: a PCR de ontem so faz sentido ao lado do antibiotico
// que ja foi suspenso. Corrigir e encerrar ou reescrever, nunca sumir.
export const RECURSOS_SEM_EXCLUSAO: readonly Recurso[] = ['auditoria', 'ficha', 'plantao', 'intercorrencia'];
